import { Button, CircleCheckIcon, TimerIcon, Typography } from '@/components/atoms';
import { HeadsetIcon } from '@/components/atoms/icons';
import { FileCheckIcon } from '@/components/atoms/icons/FileCheckIcon';
import { FileXIcon } from '@/components/atoms/icons/FileXIcon';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { UAEPassLoginButton } from '@/components/molecules/UAEPassLoginButton';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect } from 'react';
import { BackHandler, StyleSheet, View } from 'react-native';
import { useRegistrationStatus } from './hooks/useRegistrationStatus';
import { RegistrationStorage } from './storage/registrationStorage';
import { useManualRegistrationStore } from './store/registrationStore';

export interface RegistrationSuccessScreenProps {
  onContinue?: () => void;
  onBackPress?: () => void;
  title?: string;
  subtitle?: string;
  buttonTitle?: string;
  showBackButton?: boolean;
  showLanguageSwitch?: boolean;
}

export const RegistrationSuccessScreen = React.memo<RegistrationSuccessScreenProps>(
  function RegistrationSuccessScreen({
    onContinue,
    onBackPress,
    title,
    subtitle,
    buttonTitle,
    showBackButton = false,
    showLanguageSwitch = true
  }) {
    useCatalogDynatraceView(
      DT_SCREEN.REGISTRATION_SUCCESS,
      'registration_success_viewed',
      true,
    );
    const router = useRouter();
    const colors = useUIColors();
    const { t } = useTranslation();
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    const manualRegistration = useManualRegistrationStore((state) => state.registrationResponse);
    const [isNavigating, setIsNavigating] = React.useState(false);

    // Determine if this is manual registration or UAE Pass registration
    // Store this in a ref to prevent it from changing after navigation starts
    // Also check stored registration type (in case data was cleared after approval)
    const [storedRegistrationType, setStoredRegistrationType] = React.useState<'manual' | 'uae-pass' | null>(null);
    
    React.useEffect(() => {
      // Load stored registration type (in case data was cleared after approval)
      void RegistrationStorage.getRegistrationType().then(type => {
        if (type) {
          setStoredRegistrationType(type);
        }
      });
    }, []);
    
    const isManualRegistrationRef = React.useRef(!!manualRegistration && !uaePassRegistration);
    React.useEffect(() => {
      // If we have active registration data, use that
      if (manualRegistration || uaePassRegistration) {
        isManualRegistrationRef.current = !!manualRegistration && !uaePassRegistration;
      } else if (storedRegistrationType) {
        // If data was cleared but we have stored type, use that
        isManualRegistrationRef.current = storedRegistrationType === 'manual';
      }
    }, [manualRegistration, uaePassRegistration, storedRegistrationType]);
    
    const isManualRegistration = isManualRegistrationRef.current;

    // Fetch registration status (only calls API if uaePassRegistration data is available)
    // The hook automatically calls the API when the screen loads if all required data is present
    const { registrationStatus, refresh } = useRegistrationStatus();

    // Ensure API is called whenever screen comes into focus (including first load)
    // Only refresh if we have the required data (this screen is used in multiple places)
    useFocusEffect(
      useCallback(() => {
        // Check for UAE Pass registration data
        const hasUaePassData =
          uaePassRegistration?.registrationId &&
          uaePassRegistration?.customerId &&
          uaePassRegistration?.user?.idn &&
          uaePassRegistration?.user?.nationalityEN;

        // Check for manual registration data (stored in AsyncStorage)
        const hasManualRegistrationData = manualRegistration?.customer_id && manualRegistration?.registration_id;

        if (hasUaePassData || hasManualRegistrationData) {
          // Trigger API call on every screen focus to ensure latest status
          logger.debug('RegistrationSuccessScreen:Screen focused, refreshing registration status', {
            isManualRegistration,
            hasUaePassData,
            hasManualRegistrationData,
            hasRegistrationId: !!uaePassRegistration?.registrationId || !!manualRegistration?.registration_id,
            hasCustomerId: !!uaePassRegistration?.customerId || !!manualRegistration?.customer_id,
          });
          refresh();
        } else {
          logger.debug('RegistrationSuccessScreen:Screen focused, but missing required data, skipping API call', {
            isManualRegistration,
            hasUaePassData,
            hasManualRegistrationData,
            hasRegistrationId: !!uaePassRegistration?.registrationId || !!manualRegistration?.registration_id,
            hasCustomerId: !!uaePassRegistration?.customerId || !!manualRegistration?.customer_id,
          });
        }
      }, [refresh, uaePassRegistration?.registrationId, uaePassRegistration?.customerId, uaePassRegistration?.user?.idn, uaePassRegistration?.user?.nationalityEN, manualRegistration?.customer_id, manualRegistration?.registration_id, isManualRegistration])
    );

    // Save registration success state when screen loads (for both flows)
    useEffect(() => {
      const shouldSave = (uaePassRegistration?.registrationId && uaePassRegistration?.customerId) ||
        (manualRegistration?.customer_id && manualRegistration?.registration_id);

      if (shouldSave) {
        RegistrationStorage.setReachedRegistrationSuccess().catch((error) => {
          // Log error but don't block the UI
          logger.error('RegistrationSuccessScreen:Failed to save registration success state', error);
        });
      }
    }, [uaePassRegistration?.registrationId, uaePassRegistration?.customerId, manualRegistration?.customer_id, manualRegistration?.registration_id]);

    useEffect(() => {
      const backAction = () => {
        BackHandler.exitApp();
        return true;
      };

      const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);

      return () => backHandler.remove();
    }, []);

    /**
     * Clear all registration data and uploaded images
     * Called when user presses login button to start fresh
     */
    const clearRegistrationData = useCallback(async () => {
      try {
        logger.info('RegistrationSuccessScreen', 'Clearing registration data and uploaded images');

        // Clear manual registration store (includes step, formData, registrationResponse, images, upload status)
        await useManualRegistrationStore.getState().clearAll();

        // Clear UAE Pass registration
        useAppStore.getState().clearUaePassRegistration();

        // Clear all registration storage (status, IDs, success state, type)
        await RegistrationStorage.clearAll();

        logger.info('RegistrationSuccessScreen', 'Registration data and images cleared successfully');
      } catch (error) {
        logger.error('RegistrationSuccessScreen', 'Failed to clear registration data', error);
      }
    }, []);

    const handleContinue = () => {
      // Prevent multiple clicks and button switching
      if (isNavigating) return;
      setIsNavigating(true);

      trackCatalogEvent({
        screen: DT_SCREEN.REGISTRATION_SUCCESS,
        actionName: 'registration_success_continue',
        eventType: 'action',
      });
      // Navigate first to prevent button switching
      if (onContinue) {
        onContinue();
      } else {
        // Navigate to UAE Pass auth screen (same as welcome screen)
        router.replace('/uaepass');
      }

      // Clear registration data after navigation in a non-blocking way
      // Use setTimeout to defer clearing until after navigation completes
      // This prevents store state changes from triggering Fast Refresh during navigation
      setTimeout(() => {
        clearRegistrationData().catch((error) => {
          logger.error('RegistrationSuccessScreen', 'Failed to clear registration data after navigation', error);
        });
      }, 0);
    };

    const handleManualLogin = () => {
      // Prevent multiple clicks and button switching
      if (isNavigating) return;
      setIsNavigating(true);

      // Navigate first to prevent button switching
      // Pass 'from=registration-success' so login screen knows to go back to welcome on back press
      router.push('/login?from=registration-success');

      // Clear registration data after navigation in a non-blocking way
      // Use setTimeout to defer clearing until after navigation completes
      // This prevents store state changes from triggering Fast Refresh during navigation
      setTimeout(() => {
        clearRegistrationData().catch((error) => {
          logger.error('RegistrationSuccessScreen', 'Failed to clear registration data after navigation', error);
        });
      }, 0);
    };

    const handleRegisterAgain = () => {
      // Prevent multiple clicks
      if (isNavigating) return;
      setIsNavigating(true);

      // Navigate first to prevent button switching and app restart
      try {
        if (router.canGoBack()) {
          router.dismissAll();
        }
      } catch (error) {
        logger.debug('RegistrationSuccessScreen', 'dismissAll failed, using replace only', error);
      }
      router.replace('/welcome');

      // Clear registration data after navigation in a non-blocking way
      // Use setTimeout to defer clearing until after navigation completes
      // This prevents store state changes from triggering Fast Refresh during navigation
      setTimeout(() => {
        clearRegistrationData().catch((error) => {
          logger.error('RegistrationSuccessScreen', 'Failed to clear registration data after navigation', error);
        });
      }, 0);
    };

    const handleFAQSupport = () => {
      // Navigate to support screen (service request)
      router.push('/service-request');
    };

    // Determine current state based on registrationStatus
    const isApproved = registrationStatus === 'approved';
    const isRejected = registrationStatus === 'rejected';

    // Only show UAE Pass login button if status is "approved" and it's UAE Pass flow
    // Note: registrationStatus is stored in lowercase by useRegistrationStatus hook
    // Don't show buttons if we're already navigating
    const showUAEPassButton = !isNavigating && !isManualRegistration && isApproved;

    // Show manual login button if status is "approved" and it's manual registration flow
    const showManualLoginButton = !isNavigating && isManualRegistration && isApproved;

    // Show rejected state buttons
    const showRejectedButtons = !isNavigating && isRejected;

    // Determine icon, title, and subtitle based on state
    let displayIcon: React.ReactNode;
    let displayTitle: string;
    let displaySubtitle: string;

    if (isRejected) {
      // Rejected state
      displayIcon = <FileXIcon size={80} color={colors.fontPrimary} />;
      displayTitle = title || t('auth.registrationAttemptFailed');
      displaySubtitle = subtitle || t('auth.tryRegisteringAgain');
    } else if (isApproved) {
      // Success state (approved)
      displayIcon = <FileCheckIcon size={80} color={colors.fontPrimary} />;
      displayTitle = title || t('auth.registrationWasSuccessful');
      displaySubtitle = subtitle || t('auth.setupPasswordToComplete');
    } else {
      // Submitted state (under review)
      displayIcon = isManualRegistration ? (
        <TimerIcon size={80} color={colors.fontPrimary} />
      ) : (
        <CircleCheckIcon size={80} color={colors.fontPrimary} />
      );
      displayTitle = title || (isManualRegistration
        ? t('auth.registrationRequestSubmitted')
        : t('auth.registrationSuccessful'));
      displaySubtitle = subtitle || (isManualRegistration
        ? t('auth.weWillConfirmDetails')
        : t('auth.almostThere'));
    }

    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
        <LogoHeader
          showBackButton={showBackButton}
          showLanguageSwitch={showLanguageSwitch}
          onBackPress={onBackPress}
        />

        <View style={styles.content}>
          <View style={styles.mainContent}>
            <View style={styles.iconContainer}>
              {displayIcon}
            </View>

            <View style={styles.textContainer}>
              <Typography
                variant="title1"
                color={isManualRegistration ? 'fontPrimary' : 'primary'}
                style={styles.title}
              >
                {displayTitle}
              </Typography>

              <Typography
                variant="body"
                color={isManualRegistration ? 'fontPrimary' : 'fontSecondary'}
                style={styles.subtitle}
              >
                {displaySubtitle}
              </Typography>
            </View>
          </View>

          {showUAEPassButton && (
            <View style={styles.buttonContainer}>
              {/* Official UAE PASS button size/artwork, same as web's LoginDialog */}
              <UAEPassLoginButton onPress={handleContinue} />
            </View>
          )}

          {showManualLoginButton && (
            <View style={styles.buttonContainer}>
              <Button
                title={buttonTitle || t('auth.login')}
                onPress={handleManualLogin}
                variant="primary"
                fullWidth
              />
            </View>
          )}

          {showRejectedButtons && (
            <View style={styles.buttonContainer}>
              <View style={styles.rejectedButtonsContainer}>
                {/* FAQ & Support button (text variant with icon) */}
                <Button
                  title={t('welcome.faqSupport')}
                  onPress={handleFAQSupport}
                  variant="text"
                  leading={<HeadsetIcon size={18} color={colors.fontPrimary} />}
                  fullWidth
                  style={styles.faqButton}
                />
              </View>
              {/* Register button (primary) */}
              <Button
                title={t('auth.register')}
                onPress={handleRegisterAgain}
                variant="primary"
                fullWidth
              />
            </View>
          )}
        </View>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 32,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mainContent: {
    flex: 1,
    paddingTop: 32,
    alignItems: 'center',
    gap: 32,
  },
  iconContainer: {
    alignItems: 'center',
  },
  textContainer: {
    alignItems: 'center',
    gap: 32,
  },
  title: {
    textAlign: 'center',
    fontSize: 28,
    fontWeight: '500',
  },
  subtitle: {
    textAlign: 'center',
    lineHeight: 24,
    fontSize: 16,
  },
  buttonContainer: {
    width: '100%',
    paddingBottom: 45,
    gap: 12,
  },
  rejectedButtonsContainer: {
    width: '100%',
    marginBottom: 12,
  },
  faqButton: {
    marginBottom: 0,
  },
});

export default RegistrationSuccessScreen;